import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layouts";
import { useSessionStorage } from "@/hooks";
import { getCookie } from "@/lib/get-cookie";
import { blockRedirect, clearBlockedRedirect, isRedirectLoop } from "@/lib/redirect-loop";
import { parsedRedirectUrlFromSearch } from "@/lib/search-params";

import { LoginForm } from "./LoginForm";

const labelMapFromDomain = {
  boost: "SkillPanel",
  score: "SkillCheck",
};
const loginLabel = (redirectUrl: string | null) => {
  if (redirectUrl?.includes("app.")) {
    return labelMapFromDomain.boost;
  } else if (redirectUrl?.includes("score.")) {
    return labelMapFromDomain.score;
  } else {
    return "";
  }
};

const isProduction = import.meta.env.PROD;

export const LoginView = () => {
  const [, writeRedirectUrl, clearRedirectUrl] = useSessionStorage("redirectUrl");
  const [, writeRedirectTimestamp] = useSessionStorage("redirectUrlTimestamp");
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const redirectUrl = parsedRedirectUrlFromSearch();
  const isLoggedIn = getCookie("id_token");
  const navigate = useNavigate();
  const shouldNavigate =
    isLoggedIn && isProduction && !new URLSearchParams(window.location.search).has("error");

  // Read error message from URL (after SAML authentication failure)
  const errorMessage = searchParams.get("error_message");

  useEffect(
    () => {
      if (redirectUrl?.startsWith("http")) {
        if (isRedirectLoop(redirectUrl)) {
          // The product we just auto-redirected to bounced the user straight
          // back (e.g. 401 for an account it does not know). Re-arming the
          // redirect would cause an infinite loop - block it instead.
          console.debug(
            `[LoginView] Redirect loop detected for ${redirectUrl}, blocking auto-redirect`,
          );
          clearRedirectUrl();
          blockRedirect(redirectUrl);
        } else {
          clearBlockedRedirect();
          writeRedirectUrl(redirectUrl);
          writeRedirectTimestamp(Date.now());
        }
      } else {
        clearRedirectUrl();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Must call navigate() inside useEffect() to avoid "Cannot update a component
  // (`RouterProvider`) while rendering a different component (`LoginView`)" error.
  useEffect(() => {
    if (shouldNavigate) {
      console.debug("[LoginView] User is already logged in via cookie, navigating to /");
      navigate("/");
    }
  }, [shouldNavigate, navigate]);

  if (shouldNavigate) {
    return null;
  }

  return (
    <AuthLayout
      title={["Log in to your", loginLabel(redirectUrl), "account"].filter(Boolean).join(" ")}
    >
      <LoginForm initialEmail={email} redirectUrl={redirectUrl} errorMessage={errorMessage} />
    </AuthLayout>
  );
};
